import { useState } from 'react'
import axios from 'axios'
import useDirectory from 'hooks/useDirectory'

import useMutation from '../../../../hooks/useMutation'

export enum TourUploadImagePath {
  LOGO = 'logo',
  BUSINESS = 'business',
  TRIPOD = 'tripod',
  LOCATION = 'location'
}

type Props = {
  buildingId: number
  path: TourUploadImagePath
  selectedFile?: File | null
}

export default function useTourUploadImage({
  buildingId,
  path,
  selectedFile
}: Props) {
  const { directory } = useDirectory()
  const isCommunity = directory === 'community'
  const apiPrefix = isCommunity ? '/v1/tour' : '/v1'
  const [isUploading, setIsUploading] = useState(false)
  const { trigger: getPresignedUrl } = useMutation(
    `${apiPrefix}/buildings/custom-presigned-urls`
  )

  const uploadFile = async (callbacks: {
    onSuccess?: ({ key }: { key: string }) => void
    onError?: (error: any) => void
  }) => {
    try {
      const response = await getPresignedUrl({
        data: {
          buildingId,
          path
        }
      })

      const paths = response.data?.s3PreSignLinks[0]
      if (!paths) {
        callbacks.onError?.(new Error('Failed to get presigned URL.'))
        return
      }

      if (!selectedFile) {
        callbacks.onError?.(new Error('No file selected for upload.'))
        return
      }

      try {
        setIsUploading(true)
        await axios.put(paths.presignedUrl, selectedFile)
        callbacks.onSuccess?.(paths.key) // Pass the original response data if needed
      } catch (error: any) {
        callbacks.onError?.(error)
      } finally {
        setIsUploading(false)
      }
    } catch (error: any) {
      callbacks.onError?.(error)
    }
  }
  /**
   * Uploads a file to a presigned URL and handles success and error callbacks.
   * @param file The file to be uploaded.
   * @param callbacks Callbacks for success and error handling.
   * @returns
   */
  const uploadFileDescription = async (
    file: File,
    callbacks?: {
      onSuccess?: (key: string) => void
      onError?: (error: any) => void
    }
  ) => {
    try {
      const response = await getPresignedUrl({
        data: {
          buildingId,
          path
        }
      })

      if (!response || !response.data) {
        throw new Error('Failed to get presigned URL from server.')
      }

      const paths = response.data.s3PreSignLinks?.[0] // Access the first element safely
      if (!paths) {
        callbacks?.onError?.(
          new Error('Failed to get presigned URL (empty paths).')
        )
        return
      }

      if (!file) {
        callbacks?.onError?.(new Error('No file selected for upload.'))
        return
      }

      try {
        setIsUploading(true)
        await axios.put(paths.presignedUrl, file)
        callbacks?.onSuccess?.(paths.key) // Pass only the key
        return paths.key // Return the key for further use if needed
      } catch (error: any) {
        callbacks?.onError?.(error)
        throw error // Re-throw the error to be caught by the outer catch if needed
      } finally {
        setIsUploading(false)
      }
    } catch (error: any) {
      callbacks?.onError?.(error)
      throw error // Re-throw the error for further handling
    }
  }

  return { uploadFile, uploadFileDescription, isUploading }
}
